import React from 'react';
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';
import { cn } from 'lib/utils';
import { useExamInsights } from '../context';
import { UnlockButton } from './UnlockButton';

interface InsightsUnlockBannerProps {
  /** Analyses left in the learner's running plan; 0 without a plan. */
  analysesRemaining: number;
  analysisLimit: number;
  /** Writing/Speaking went past the free monthly AI grading: the unlock grades it too. */
  aiGradingPending?: boolean;
  className?: string;
}

/** Above a locked IELTS/SAT review: the score is free, this is what the plan adds and how to get it. */
export function InsightsUnlockBanner({ analysesRemaining, analysisLimit, aiGradingPending = false, className }: InsightsUnlockBannerProps) {
  const { t } = useTranslation();
  const { locked, requestUnlock, unlocking, failed } = useExamInsights();
  if (!locked) return null;
  const canSpend = analysesRemaining > 0;

  return (
    <section
      aria-label={t('exam_insights.banner_title')}
      className={cn(
        'relative my-4 flex flex-col gap-6 overflow-hidden rounded-3xl bg-[#0F1226] p-6 text-white sm:flex-row sm:items-center sm:justify-between sm:px-8 sm:py-7',
        className,
      )}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(600px_220px_at_85%_0%,rgba(219,39,119,0.45),transparent_70%),radial-gradient(500px_220px_at_10%_120%,rgba(79,43,224,0.5),transparent_70%)]"
      />
      <div className="relative flex min-w-0 items-start gap-4 sm:max-w-[640px]">
        <span className="flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-[14px] bg-[linear-gradient(135deg,#4F2BE0,#DB2777)] text-white">
          <Lock className="h-[22px] w-[22px]" aria-hidden />
        </span>
        <div className="min-w-0">
          <p className="font-mono text-[10.5px] font-bold uppercase tracking-[0.14em] text-[#C9CCE6]">{t('exam_insights.locked_label')}</p>
          <p className="mt-1.5 font-display text-xl font-extrabold leading-[1.15] tracking-[-0.03em] sm:text-2xl">{t('exam_insights.banner_title')}</p>
          <p className="mt-2 text-sm leading-relaxed text-[#C9CCE6]">{t('exam_insights.banner_body')}</p>
          {aiGradingPending && <p className="mt-2 text-sm font-medium leading-relaxed text-primary">{t('exam_insights.grading_pending')}</p>}
        </div>
      </div>
      <div className="relative flex shrink-0 flex-col items-stretch gap-2 text-center sm:items-center">
        <UnlockButton size="lg" onClick={requestUnlock} loading={unlocking} className="rounded-[14px]">
          {canSpend ? t('exam_insights.unlock_now') : t('exam_insights.see_plans')}
        </UnlockButton>
        <p className="text-[12.5px] text-[#B8BCDA]">
          {canSpend ? t('exam_insights.credits_left', { left: analysesRemaining, total: analysisLimit }) : t('exam_insights.plans_hint')}
        </p>
        {failed && (
          <p role="alert" className="text-xs text-red-300">
            {t('exam_insights.unlock_failed')}
          </p>
        )}
      </div>
    </section>
  );
}
